import { QueryClient, QueryKey } from '@tanstack/react-query';
import { formatDate } from '@utils/dateHelpers';
import {
  WorkloadBasicForeman,
  WorkloadCalendarRespSuccess,
  WorkloadCalendarSchedule,
} from './types';

export interface OptimisticScheduleDrop {
  drag: WorkloadCalendarSchedule;
  dropForeman?: WorkloadBasicForeman;
  dropDate: Date;
}

export const toOptimisticSchedule = (
  drag: WorkloadCalendarSchedule,
  dropDate: Date,
): WorkloadCalendarSchedule => {
  const schedule = { ...drag, startDate: formatDate(dropDate, 'yyyy-MM-dd'), dragLocked: true };
  if (drag.id !== 'new') return schedule;

  const { id, projectId, division, stageName, workflow } = drag.job;
  return {
    ...schedule,
    requirements: undefined,
    relatedSchedules: undefined,
    job: { id, projectId, division, stageName, workflow, name: '', address: '' },
  };
};

export const applyOptimisticScheduleDrop = (
  data: WorkloadCalendarRespSuccess | undefined,
  { drag, dropForeman, dropDate }: OptimisticScheduleDrop,
): WorkloadCalendarRespSuccess | undefined => {
  if (!data) return data;

  const droppedSchedule = toOptimisticSchedule(drag, dropDate);

  return {
    ...data,
    withoutTeam: data.withoutTeam.filter((schedule) => schedule.id !== drag.id),
    records: data.records.map((foreman) => {
      if (dropForeman && foreman.id === dropForeman.id) {
        return {
          ...foreman,
          schedules: [...foreman.schedules.filter((s) => s.id !== drag.id), droppedSchedule],
        };
      }
      return { ...foreman, schedules: foreman.schedules.filter((s) => s.id !== drag.id) };
    }),
  };
};

export type PreviousScheduleQueriesData = Array<
  [QueryKey, WorkloadCalendarRespSuccess | undefined]
>;

export const restoreScheduleQueriesData = (
  queryClient: QueryClient,
  previousData?: PreviousScheduleQueriesData,
) => {
  previousData?.forEach(([queryKey, data]) =>
    queryClient.setQueryData<WorkloadCalendarRespSuccess>(queryKey, data),
  );
};
